import axios, { AxiosResponse, isAxiosError } from "axios";
import appConfig from "../appConfig";
import { generateUUID } from "../utils";
import {
  APIResponse,
  CreateMicroSiteResponse,
  CustomizeMicrositePayload,
  PeopleBusinessInfoRequest,
  PeopleBusinessInfoResponse,
  PollCreateMicroSiteResponse
} from "./types";

class ApiService {
  private axiosInstance = axios.create({
    baseURL: "/api-proxy/sales-portal/sales-portal/api/v1/microsite/"
    // baseURL: import.meta.env.DEV
    //   ? "http://localhost:8080/bicycle-ai/api/v1/microsite"
    //   : "/api-proxy/bicycle-ai/bicycle-ai/api/v1/microsite"
  });

  private axiosBizEntityInstance = axios.create({
    baseURL: "/api-proxy/api/preview/bizEntity/"
  });

  private axiosBicycleAIInstance = axios.create({
    baseURL: "/api-proxy/bicycle-ai/bicycle-ai/api/v1"
  });
  constructor() {
    this.axiosInstance.interceptors.request.use(config => {
      config.headers[appConfig.requestIdHeader] = generateUUID();
      config.headers["Authorization"] = `Bearer ${appConfig.apiToken}`;

      return config;
    });

    this.axiosBicycleAIInstance.interceptors.request.use(config => {
      config.headers[appConfig.requestIdHeader] = generateUUID();
      config.headers["Authorization"] = `Bearer ${appConfig.apiToken}`;
      return config;
    });

    this.axiosBizEntityInstance.interceptors.request.use(config => {
      config.headers[appConfig.requestIdHeader] = generateUUID();
      config.headers["Authorization"] = `Bearer ${appConfig.apiToken}`;
      return config;
    });
  }

  createCompanyMicrosite(
    companyName: string,
    customPrompt: string,
    companyDomain?: string,
    companyDescription?: string
  ) {
    const fetchFn = async () => {
      const payload = {
        company_name: companyName,
        prompt: customPrompt,
        company_domain: companyDomain,
        company_description: companyDescription
      };

      console.log("[API] Sending request to /create with payload:", JSON.stringify(payload, null, 2));

      const response = await this.axiosInstance.post<CreateMicroSiteResponse>("/create", payload);

      console.log("[API] Received response from /create:", JSON.stringify(response.data, null, 2));

      return response;
    };

    return this.call(fetchFn);
  }

  regenerateCompanyMicrosite(companyName: string, payload?: CustomizeMicrositePayload & { poll_id: string }) {
    const fetchFn = () =>
      this.axiosInstance.post<CreateMicroSiteResponse>("/regenerate", {
        company_name: companyName,
        prompt: payload?.customPrompt || "",
        user_role: payload?.userRole || "",
        business_unit: payload?.businessUnit || "",
        poll_id: payload?.poll_id || "",
        isSlides: payload?.isSlides || false,
        account_id: payload?.accountId || "",
        opportunity_id: payload?.opportunityId || ""
      });

    return this.call(fetchFn);
  }

  saveUserAnalyticsData(payload: any): Promise<any> {
    const fetchFn = async () => {
      const response = await this.axiosBizEntityInstance.post<any>("data-capture", payload);

      return response;
    };

    return this.call(fetchFn);
  }

  getCreateCompanyMicrositeStatus(pollId: string) {
    const fetchFn = () =>
      this.axiosInstance.get<PollCreateMicroSiteResponse>("/status", {
        params: {
          pollId
        }
      });

    return this.call(fetchFn);
  }

  getUserBusinessInfo(payload: PeopleBusinessInfoRequest) {
    const fetchFn = () =>
      this.axiosBicycleAIInstance.post<PeopleBusinessInfoResponse>("/get_people_business_info", payload);

    return this.call(fetchFn);
  }

  private isErrorResponse(statusCode: number) {
    return statusCode >= 400 && statusCode < 500;
  }

  private async call<R>(promiseFn: () => Promise<AxiosResponse<R>>) {
    const response: APIResponse<R> = {
      data: null as R,
      error: false,
      message: ""
    };

    try {
      const { data, status, statusText } = await promiseFn();

      response.data = data;
      if (this.isErrorResponse(status)) {
        response.error = true;
        response.message = statusText;
      } else {
        response.data = data;
      }
    } catch (err: any) {
      let message = err?.message || err?.data?.message || String(err);
      if (isAxiosError(err)) {
        message = err.response?.data?.message || err?.response?.statusText || err.message || String(err);
      }

      response.error = true;
      response.message = message;
    }

    return response;
  }
}

export const apiService = new ApiService();
